import StyledSlider from "common/Slider/StyledSlider";
import Input from "common/Inputs/Input";
import { useEffect, useState } from "react";
import { grey } from "@ant-design/colors";
import useInput from "utils/hooks/useInput";
import { ValidationError } from "features/admin/group/InvitationModal/styles";
import {
  SelectorHelp,
  SelectorInput,
  SelectorLabel,
  SelectorValueName,
  SelectorWrapper,
} from "features/admin/group/InvitationModal/InvitationSelectors/styles";
import { useTheme } from "styled-components";
import { getUpdatedMarks } from "features/admin/group/InvitationModal/InvitationSelectors/helpers";
import Text from "common/Typography/Text";
import Title from "common/Typography/Title";
import { typography } from "@shared/common/Themes/typography";
import { InfinityIcon } from "common/Icons/Icons";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
const MARKS = {
  0: "1",
  25: "10",
  50: "50",
  75: "100",
  100: {
    label: <InfinityIcon style={{ fontSize: 20 }} />,
  },
};

const useNumUsersSelector = (invitation) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const { isNewLook } = useTheme();
  const [selectedNumUsers, setSelectedNumUsers] = useState(
    invitation && invitation.num_users
  );
  const [marks, modifiedMarkKey] = getUpdatedMarks(MARKS, selectedNumUsers);
  const { value: selectedTelegramId, bind: bindTelegramId } = useInput(
    (invitation && invitation.telegram_user_id) || ""
  );
  const { value: selectedTelegramUsername, bind: bindTelegramUsername } =
    useInput((invitation && invitation.username) || "");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (
      invitation &&
      selectedNumUsers &&
      selectedNumUsers < invitation.accepted_by.length
    ) {
      setError(
        t("invitations.modal.errorNumUsersLessThanAccepted", {
          count: invitation.accepted_by.length,
        })
      );
    } else if (selectedTelegramId && selectedTelegramUsername) {
      setError(t("invitations.modal.errorBothProvided"));
    } else {
      setError(null);
    }
  }, [
    selectedTelegramId,
    selectedTelegramUsername,
    selectedNumUsers,
    invitation,
    t,
  ]);

  const getNumUsersFromMarkKey = (sliderValue) => {
    if (sliderValue === 100) {
      return null;
    }
    return parseInt(marks[sliderValue]);
  };

  const changeNumUsers = (value) => {
    let updatedNumUsers = value;
    if (updatedNumUsers < 1) {
      updatedNumUsers = null;
    }
    setSelectedNumUsers(updatedNumUsers);
  };

  // New look: the question as a label, the slider and the value in a well,
  // the optional invitee fields and helper text under it.
  const newLookComponent = (
    <div>
      <SelectorLabel>{t("invitations.modal.numUsersLabel")}</SelectorLabel>
      <SelectorWrapper>
        <StyledSlider
          marks={marks}
          min={0}
          max={100}
          step={25}
          tooltip={{ open: false }}
          value={modifiedMarkKey === null ? 100 : modifiedMarkKey}
          onChange={(value) => {
            changeNumUsers(getNumUsersFromMarkKey(value));
          }}
        />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingTop: "8px",
          }}
        >
          <SelectorValueName>
            {t("invitations.modal.numUsersTitle")}
          </SelectorValueName>
          <SelectorInput
            bordered={false}
            style={{
              width: "100px",
              textAlign: "end",
              padding: "0",
            }}
            placeholder={t("invitations.modal.unlimited")}
            value={selectedNumUsers}
            onChange={changeNumUsers}
          />
        </div>
      </SelectorWrapper>
      {selectedNumUsers === 1 && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginTop: 12,
            }}
          >
            <Input
              placeholder={t("invitations.modal.telegramUsername")}
              {...bindTelegramUsername}
            />
            <SelectorHelp style={{ marginTop: 0, flex: "none" }}>
              {t("invitations.modal.or")}
            </SelectorHelp>
            <Input
              placeholder={t("invitations.modal.telegramId")}
              {...bindTelegramId}
            />
          </div>
          <SelectorHelp>
            {t("invitations.modal.telegramIdUsernameHelp")}
          </SelectorHelp>
        </>
      )}
      {error && <ValidationError>{error}</ValidationError>}
    </div>
  );

  const numUserSelectorComponent = isNewLook ? (
    newLookComponent
  ) : (
    <div>
      <Text>{t("invitations.modal.numUsersLabel")}</Text>
      <SelectorWrapper>
        <StyledSlider
          marks={marks}
          min={0}
          max={100}
          step={25}
          tooltip={{ open: false }}
          value={modifiedMarkKey === null ? 100 : modifiedMarkKey}
          onChange={(value) => {
            changeNumUsers(getNumUsersFromMarkKey(value));
          }}
        />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingTop: "12px",
          }}
        >
          <Title level={4} style={{ fontWeight: 500 }}>
            {t("invitations.modal.numUsersTitle")}
          </Title>
          <SelectorInput
            bordered={false}
            style={{
              width: "100px",
              textAlign: "end",
              padding: "0",
            }}
            placeholder={t("invitations.modal.unlimited")}
            value={selectedNumUsers}
            onChange={changeNumUsers}
          />
        </div>
        {selectedNumUsers === 1 && (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginTop: "16px",
              }}
            >
              <Input
                placeholder={t("invitations.modal.telegramUsername")}
                {...bindTelegramUsername}
              />
              <Text
                $size={typography.fontSizes.S}
                style={{ padding: "0 16px" }}
              >
                {t("invitations.modal.or")}
              </Text>
              <Input
                placeholder={t("invitations.modal.telegramId")}
                {...bindTelegramId}
              />
            </div>
            <Text
              style={{
                fontSize: "10px",
                color: grey[3],
                display: "block",
                marginTop: "8px",
              }}
            >
              {t("invitations.modal.telegramIdUsernameHelp")}
            </Text>
          </>
        )}
      </SelectorWrapper>
      {error && <ValidationError>{error}</ValidationError>}
    </div>
  );

  return [
    numUserSelectorComponent,
    selectedNumUsers,
    selectedTelegramUsername || null,
    selectedTelegramId || null,
    error,
  ];
};

export default useNumUsersSelector;
